import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const StyleDemo = () => {
  return (
    <div className="p-8 space-y-8">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          Careerminds Labs Style Guide
        </h1>
        <p className="text-muted-foreground mb-8">
          Careerminds-themed shadcn/ui components — navy primary, teal accent, beige surface
        </p>
      </div>

      {/* Hero Section with brand gradient */}
      <Card className="bg-gradient-cm text-white border-0">
        <CardContent className="p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Careerminds Labs</h2>
          <p className="text-lg opacity-90 mb-6">
            Track citations and grow your online presence with our modern analytics platform
          </p>
          <Button variant="secondary" size="lg">
            Get Started
          </Button>
        </CardContent>
      </Card>

      {/* Button Variations */}
      <Card>
        <CardHeader>
          <CardTitle>Button Variations</CardTitle>
          <CardDescription>Different button styles with the Careerminds theme</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button>Primary Button</Button>
            <Button variant="outline">Outline Button</Button>
            <Button variant="secondary">Secondary Button</Button>
            <Button variant="ghost">Ghost Button</Button>
            <Button variant="destructive">Destructive Button</Button>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button size="sm">Small</Button>
            <Button size="default">Default</Button>
            <Button size="lg">Large</Button>
          </div>
        </CardContent>
      </Card>

      {/* Badge Variations */}
      <Card>
        <CardHeader>
          <CardTitle>Badge Components</CardTitle>
          <CardDescription>Status indicators and labels</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="success">Success</Badge>
            <Badge variant="warning">Warning</Badge>
            <Badge variant="info">Info</Badge>
            <Badge variant="destructive">Error</Badge>
            <Badge variant="outline">Outline</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-primary">Brand Citations</CardTitle>
            <CardDescription>Track your brand mentions</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-primary">156</div>
            <p className="text-sm text-muted-foreground">+12% from last week</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-amber-600">Competitor Analysis</CardTitle>
            <CardDescription>Compare with competitors</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">89%</div>
            <p className="text-sm text-muted-foreground">Market share</p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-lg transition-shadow">
          <CardHeader>
            <CardTitle className="text-green-600">Quality Score</CardTitle>
            <CardDescription>Citation quality rating</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">8.9/10</div>
            <p className="text-sm text-muted-foreground">Excellent quality</p>
          </CardContent>
        </Card>
      </div>

      {/* Color Palette Display */}
      <Card>
        <CardHeader>
          <CardTitle>Careerminds Color Palette</CardTitle>
          <CardDescription>Brand-aligned semantic tokens</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <div className="w-full h-16 bg-primary rounded-lg"></div>
              <p className="text-sm font-medium">Primary (Navy)</p>
              <p className="text-xs text-muted-foreground">#1D264A</p>
            </div>
            <div className="space-y-2">
              <div className="w-full h-16 bg-accent rounded-lg"></div>
              <p className="text-sm font-medium">Accent (Teal)</p>
              <p className="text-xs text-muted-foreground">#C6E8E6</p>
            </div>
            <div className="space-y-2">
              <div className="w-full h-16 bg-background rounded-lg border"></div>
              <p className="text-sm font-medium">Background (Beige)</p>
              <p className="text-xs text-muted-foreground">#FAF7F5</p>
            </div>
            <div className="space-y-2">
              <div className="w-full h-16 bg-muted rounded-lg"></div>
              <p className="text-sm font-medium">Muted</p>
              <p className="text-xs text-muted-foreground">Subtle surface</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Typography */}
      <Card>
        <CardHeader>
          <CardTitle>Typography Scale</CardTitle>
          <CardDescription>Modern, readable typography</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <h1 className="text-4xl font-bold">Heading 1</h1>
          <h2 className="text-3xl font-semibold">Heading 2</h2>
          <h3 className="text-2xl font-semibold">Heading 3</h3>
          <h4 className="text-xl font-semibold">Heading 4</h4>
          <p className="text-base">Regular paragraph text with good readability and spacing.</p>
          <p className="text-sm text-muted-foreground">Small muted text for secondary information.</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default StyleDemo;